import { useEffect } from "react";
import { useSettings } from "@/context/SettingsContext";

/**
 * Injects verification meta tags and Google Analytics 4 (gtag.js) into <head>
 * based on live settings from the admin panel. Runs client-side so no rebuild
 * is needed when values change.
 */
export default function HeadInjector() {
    const { settings } = useSettings();
    const gaId = (settings.ga_measurement_id || "").trim();

    // Meta verification tags
    useEffect(() => {
        const upsertMeta = (name, content) => {
            let el = document.querySelector(`meta[name="${name}"]`);
            if (!content) { if (el) el.remove(); return; }
            if (!el) {
                el = document.createElement("meta");
                el.setAttribute("name", name);
                document.head.appendChild(el);
            }
            el.setAttribute("content", content);
        };

        upsertMeta("google-site-verification", settings.google_site_verification || "");
        upsertMeta("msvalidate.01", settings.bing_site_verification || "");
    }, [settings.google_site_verification, settings.bing_site_verification]);

    // Google Analytics 4 — inject gtag.js when a Measurement ID is configured
    useEffect(() => {
        if (!gaId || !/^G-[A-Z0-9]+$/i.test(gaId)) return;

        // Loader script (async)
        const loader = document.createElement("script");
        loader.async = true;
        loader.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
        loader.setAttribute("data-ga4", gaId);
        document.head.appendChild(loader);

        // Init inline script
        const init = document.createElement("script");
        init.setAttribute("data-ga4-init", gaId);
        init.text = `window.dataLayer = window.dataLayer || [];
function gtag(){dataLayer.push(arguments);}
window.gtag = gtag;
gtag('js', new Date());
gtag('config', '${gaId}', { anonymize_ip: true });`;
        document.head.appendChild(init);

        return () => {
            loader.remove();
            init.remove();
        };
    }, [gaId]);

    return null;
}
